import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import {
	PurchaseOrderReceiptExceptionEventType,
	PurchaseOrderReceiptExceptionStatus,
} from '../../enums';
import { BaseEntity } from './base.entity';
import { PurchaseOrderReceiptExceptionEntity } from './purchase-order-receipt-exception.entity';

/**
 * Audit-history row for every transition or material change to a
 * `purchase_order_receipt_exception`. Mirrors the
 * PurchaseOrderApprovalEvent pattern — one row per event, insert-only,
 * actor captured via BaseEntity._creator_userId.
 *
 * Why a separate event log when BaseEntity already tracks "row was
 * modified by X at Y": BaseEntity gives us last-modified info, but
 * cannot answer "when did this hit Vendor Notified?" or "who marked
 * the credit as received?" The event log captures the semantic
 * transition (fromStatus → toStatus) so the Activity tab and aging
 * widgets have authoritative data without parsing diffs.
 */
@Index('po_receipt_exception_event__fk_exception__id_fk', ['fkExceptionId'], {})
@Index(
	'po_receipt_exception_event__exception_type_idx',
	['fkExceptionId', 'eventType'],
	{},
)
@Entity('purchase_order_receipt_exception_event')
export class PurchaseOrderReceiptExceptionEventEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_purchase_order_receipt_exception__id',
		unsigned: true,
	})
	public fkExceptionId!: number;

	@Column('varchar', { name: 'eventType', length: 32 })
	public eventType!: PurchaseOrderReceiptExceptionEventType;

	/** State the exception was in BEFORE this event. NULL on CREATED events. */
	@Column('varchar', { name: 'fromStatus', length: 32, nullable: true })
	public fromStatus!: PurchaseOrderReceiptExceptionStatus | null;

	/** State the exception is in AFTER this event. NULL on NOTE_ADDED / ASSIGNED (no state change). */
	@Column('varchar', { name: 'toStatus', length: 32, nullable: true })
	public toStatus!: PurchaseOrderReceiptExceptionStatus | null;

	/** Free-form notes accompanying the event. Required on RESOLVED / WRITTEN_OFF; optional otherwise. */
	@Column('text', { name: 'notes', nullable: true })
	public notes!: string | null;

	@ManyToOne(() => PurchaseOrderReceiptExceptionEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{
			name: 'fk_purchase_order_receipt_exception__id',
			referencedColumnName: 'id',
		},
	])
	public fkException!: PurchaseOrderReceiptExceptionEntity;
}
